import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { useMailboxProviders, MailboxProvider } from '@/hooks/useMailboxProviders'
import { Plus } from 'lucide-react'

interface AddProviderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onProviderAdded?: (provider: MailboxProvider) => void
}

export function AddProviderDialog({ open, onOpenChange, onProviderAdded }: AddProviderDialogProps) {
  const { addProvider } = useMailboxProviders()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    smtp_host: '',
    smtp_port: '',
    imap_host: '',
    imap_port: '',
    requires_app_password: false,
    setup_instructions: '',
    is_active: true
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.display_name.trim()) return

    setIsSubmitting(true)
    try {
      const provider = await addProvider({
        name: formData.name.toLowerCase().replace(/\s+/g, '_'),
        display_name: formData.display_name,
        description: formData.description || undefined,
        smtp_host: formData.smtp_host || undefined,
        smtp_port: formData.smtp_port ? parseInt(formData.smtp_port) : undefined,
        imap_host: formData.imap_host || undefined,
        imap_port: formData.imap_port ? parseInt(formData.imap_port) : undefined,
        requires_app_password: formData.requires_app_password,
        setup_instructions: formData.setup_instructions || undefined,
        is_active: formData.is_active
      })
      
      onProviderAdded?.(provider)
      onOpenChange(false)
      
      // Reset form
      setFormData({
        name: '',
        display_name: '',
        description: '',
        smtp_host: '',
        smtp_port: '',
        imap_host: '',
        imap_port: '',
        requires_app_password: false,
        setup_instructions: '',
        is_active: true
      })
    } catch (error) {
      // Error is handled in the hook
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add New Mailbox Provider
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium">
                Provider Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., gmail, custom_provider"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Unique identifier (lowercase, no spaces)
              </p>
            </div>
            <div>
              <Label htmlFor="display_name" className="text-sm font-medium">
                Display Name *
              </Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="e.g., Gmail, Custom Provider"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Name shown in dropdowns
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this email provider"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="smtp_host" className="text-sm font-medium">
                SMTP Host
              </Label>
              <Input
                id="smtp_host"
                value={formData.smtp_host}
                onChange={(e) => setFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
                placeholder="smtp.example.com"
              />
            </div>
            <div>
              <Label htmlFor="smtp_port" className="text-sm font-medium">
                SMTP Port
              </Label>
              <Input
                id="smtp_port"
                type="number"
                value={formData.smtp_port}
                onChange={(e) => setFormData(prev => ({ ...prev, smtp_port: e.target.value }))}
                placeholder="587"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="imap_host" className="text-sm font-medium">
                IMAP Host
              </Label>
              <Input
                id="imap_host"
                value={formData.imap_host}
                onChange={(e) => setFormData(prev => ({ ...prev, imap_host: e.target.value }))}
                placeholder="imap.example.com"
              />
            </div>
            <div>
              <Label htmlFor="imap_port" className="text-sm font-medium">
                IMAP Port
              </Label>
              <Input
                id="imap_port"
                type="number"
                value={formData.imap_port}
                onChange={(e) => setFormData(prev => ({ ...prev, imap_port: e.target.value }))}
                placeholder="993"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="requires_app_password"
              checked={formData.requires_app_password}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_app_password: checked }))}
            />
            <Label htmlFor="requires_app_password" className="text-sm font-medium">
              Requires App Password
            </Label>
          </div>

          <div>
            <Label htmlFor="setup_instructions" className="text-sm font-medium">
              Setup Instructions
            </Label>
            <Textarea
              id="setup_instructions"
              value={formData.setup_instructions}
              onChange={(e) => setFormData(prev => ({ ...prev, setup_instructions: e.target.value }))}
              placeholder="Instructions for users on how to configure this provider"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.name.trim() || !formData.display_name.trim()}
            >
              {isSubmitting ? 'Adding...' : 'Add Provider'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}